import {
  BaseQueryFn,
  EndpointBuilder,
  MutationDefinition,
} from '@reduxjs/toolkit/query';

import type {
  IPostTwoFactorAddChannelArgs,
  IPostTwoFactorDeactivateChannelArgs,
  IPostTwoFactorSetDefaultChannelArgs,
  IPostTwoFactorTotpQrCodeArgs,
  IPostTwoFactorTotpQrCodeResponse,
  IPostTwoFactorTriggerCodeArgs,
  IPostTwoFactorTriggerCodeResponse,
  IPostTwoFactorValidateArgs,
  IPutUserTwoFactorActivationArgs,
} from './twoFactorAuth.types';
import type { ITokenResponse } from 'services/user/user.types';

export const postTwoFactorTriggerCode = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostTwoFactorTriggerCodeArgs,
  BaseQueryFn,
  string,
  IPostTwoFactorTriggerCodeResponse
> =>
  build.mutation<
    IPostTwoFactorTriggerCodeResponse,
    IPostTwoFactorTriggerCodeArgs
  >({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/2fa/send`,
      method: 'POST',
      body: queryArg.triggerCodeBody,
    }),
    // also used during login, authenticated by the 2FA token in the body
    extraOptions: { maxRetries: 0, withoutSession: true },
  });

export const postTwoFactorValidate = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostTwoFactorValidateArgs,
  BaseQueryFn,
  string,
  ITokenResponse
> =>
  build.mutation<ITokenResponse, IPostTwoFactorValidateArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/2fa/validate`,
      method: 'POST',
      body: queryArg.validateCredentials,
    }),
    // used during login, authenticated by the 2FA token in the body
    extraOptions: { maxRetries: 0, withoutSession: true },
  });

export const postTwoFactorDeactivateChannel = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostTwoFactorDeactivateChannelArgs,
  BaseQueryFn,
  string,
  unknown
> =>
  build.mutation<unknown, IPostTwoFactorDeactivateChannelArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/2fa/channel/deactivate`,
      method: 'POST',
      body: queryArg.channelBody,
    }),
    extraOptions: { maxRetries: 0 },
  });

export const postTwoFactorSetDefaultChannel = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostTwoFactorSetDefaultChannelArgs,
  BaseQueryFn,
  string,
  unknown
> =>
  build.mutation<unknown, IPostTwoFactorSetDefaultChannelArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/2fa/channel/default`,
      method: 'POST',
      body: queryArg.channelBody,
    }),
    extraOptions: { maxRetries: 0 },
  });

export const postTwoFactorAddChannel = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostTwoFactorAddChannelArgs,
  BaseQueryFn,
  string,
  unknown
> =>
  build.mutation<unknown, IPostTwoFactorAddChannelArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/2fa/channel/add`,
      method: 'POST',
      body: queryArg.channelBody,
    }),
    extraOptions: { maxRetries: 0 },
  });

export const putTwoFactorUserActivate = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPutUserTwoFactorActivationArgs,
  BaseQueryFn,
  string,
  unknown
> =>
  build.mutation<unknown, IPutUserTwoFactorActivationArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/2fa/user/${queryArg.id}/activate`,
      method: 'PATCH',
    }),
    extraOptions: { maxRetries: 0 },
  });

export const putTwoFactorUserDeactivate = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPutUserTwoFactorActivationArgs,
  BaseQueryFn,
  string,
  unknown
> =>
  build.mutation<unknown, IPutUserTwoFactorActivationArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/2fa/user/${queryArg.id}/deactivate`,
      method: 'PATCH',
    }),
    extraOptions: { maxRetries: 0 },
  });

export const postTwoFactorTotpQrCode = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostTwoFactorTotpQrCodeArgs,
  BaseQueryFn,
  string,
  IPostTwoFactorTotpQrCodeResponse
> =>
  build.mutation<
    IPostTwoFactorTotpQrCodeResponse,
    IPostTwoFactorTotpQrCodeArgs
  >({
    invalidatesTags: [],
    query: (queryArg) => ({
      url: `api/2fa/totp/qr`,
      method: 'POST',
      body: queryArg.body,
    }),
    extraOptions: { maxRetries: 0 },
  });
